/**
 * CanvasProvider — Global state for the Resume Canvas.
 *
 * Manages:
 * - activeMode: 'LIST' | 'CANVAS' (which view is shown in right panel)
 * - activeTab: 'resume' | 'cover_letter' | 'application_note'
 * - templateId: 'modern' | 'executive' | 'technical'
 * - resumeData: Structured JSON for the resume content
 * - streamingState: Controls for token-by-token streaming
 * - agentPhase: The 7-step optimization state machine
 */

import React, {
  useCallback,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { CanvasContext } from './canvasContext';

import {
  detectLanguageFromJob,
  relabelCanvasSections,
  sectionDisplayTitle,
  type DocumentLocale,
  type DocumentLocaleSource,
} from '../utils/documentLanguage';
import {
  dedupeCanvasSections,
  findSectionIndexByKey,
  removeSectionByKey,
  sanitizeSectionContentForCanvas,
  cleanupMisplacedCanvasSections,
} from './canvasSectionUtils';
import { sortSectionsByCanonicalOrder } from '../../dashboard/components/AIResumeOptimizer/utils/parseResumeSections';
import type { CanonicalResume } from '../../resume/schemas/resumeSchema';

// ── Types ─────────────────────────────────────────────────────────────────────

export type PanelMode = 'LIST' | 'CANVAS';

export type CanvasTab = 'resume' | 'cover_letter' | 'application_note';

/** Any TEMPLATE_LIBRARY id (e.g. 'ats-optimized', 'executive', 'navy-tech'). */
export type TemplateId = string;

export type AgentPhase =
  | 'idle'
  | 'analysis'      // State 1: Show match score + gaps
  | 'consent'       // State 2: Ask permission to rewrite
  | 'optimization'  // State 3: Streaming rewrite to canvas
  | 'refinement'    // State 4: Ask for feedback on changes
  | 'template'      // State 5: Template selection
  | 'followup'      // State 6: Offer cover letter
  | 'fulfillment';  // State 7: Save/download/email actions

export interface ResumeSection {
  id: string;
  title: string;
  content: string;
  isStreaming?: boolean;
}

/** Lightweight canvas render unit — plain text per section (same shape as parseResumeSections output). */
export interface CanvasSection {
  title: string;
  content: string;
}

export interface ResumeData {
  name: string;
  headline?: string;
  contact?: {
    email?: string;
    phone?: string;
    location?: string;
    linkedin?: string;
    github?: string;
    portfolio?: string;
  };
  summary?: string;
  skills?: string[];
  experience?: Array<{
    title: string;
    company: string;
    location?: string;
    startDate: string;
    endDate?: string;
    bullets: string[];
  }>;
  education?: Array<{
    degree: string;
    institution: string;
    year: string;
    gpa?: string;
  }>;
  certifications?: string[];
  projects?: Array<{
    name: string;
    description: string;
    technologies?: string[];
  }>;
  metrics?: Array<{
    label: string;
    value: string;
  }>;
}

export interface JobContext {
  id: string;
  title: string;
  company: string;
  description?: string;
  skills?: string[];
  requirements?: string[];
  /** External listing URL for Assisted Handoff (Apply opens this in a new tab). */
  url?: string | null;
  location?: string | null;
  sourceChannel?: string | null;
  /** Pre-computed score from the recommendation engine (0-100). When present
   * this is shown in the chat instead of the local re-computed score, which can
   * be unreliable when the job has an empty skills array. */
  precomputedMatchScore?: number;
}

export interface AnalysisResult {
  matchScore: number;
  gaps: string[];
  strengths: string[];
  suggestedTemplate: string; // Any TEMPLATE_LIBRARY id
  /** Skills from the JD that were found in the resume (green) */
  keywordHits: string[];
  /** Skills from the JD that are missing from the resume (yellow/red) */
  keywordMisses: string[];
}

export interface CanvasState {
  mode: PanelMode;
  tab: CanvasTab;
  templateId: string;
  phase: AgentPhase;
  job: JobContext | null;
  /** Structured Gemini JSON used by ResumeCanvas (bypasses regex render). */
  canonicalResume: CanonicalResume | null;
  /** Snapshot of sections before optimization started — used for diff/Show Changes view. */
  originalSections: CanvasSection[] | null;
  /** Original full profile data kept for LLM context (not for rendering). */
  originalResumeData: ResumeData | null;
  rawResumeText: string;
  analysis: AnalysisResult | null;
  /**
   * The match score captured at the moment of initial analysis (before any
   * optimization). `analysis.matchScore` is updated to the post-optimization
   * score, so we need this as a permanent "before" snapshot to show the
   * gamified improvement (e.g. "59% → 87%").
   */
  originalMatchScore: number | null;
  streamingSection: string | null;
  streamingContent: string;
  /** Sections queued for optimization but not yet started (show skeleton loaders). */
  pendingSections: string[];
  coverLetter: string | null;
  applicationNote: string | null;
  selectedJobs: JobContext[];
  activeVersionIndex: number;
  versions: Array<{ job: JobContext; sections: CanvasSection[] }>;
  isSaving: boolean;
  isSaved: boolean;
  isApplied: boolean;
  /** Last explicitly saved / loaded resume snapshot — draft edits diff against this. */
  committedSections: CanvasSection[] | null;
  /** Language for resume / letters (from JD auto-detect or user override). */
  documentLocale: DocumentLocale;
  documentLocaleSource: DocumentLocaleSource;
  /** True when canvas sections differ from committedSections. */
  isDraftDirty: boolean;
  /** Section title currently being optimized — used for scroll-to + highlight in canvas. */
  activeSectionTitle?: string;
}

type CanvasAction =
  | { type: 'SET_MODE'; mode: PanelMode }
  | { type: 'SET_TAB'; tab: CanvasTab }
  | { type: 'SET_TEMPLATE'; templateId: string }
  | { type: 'SET_PHASE'; phase: AgentPhase }
  | { type: 'SET_JOB'; job: JobContext }
  /** Replace all canvas sections at once (used by startOptimization). */
  | { type: 'SET_SECTIONS'; sections: CanvasSection[] }
  /** Replace sections without rebucketing — use for canonical JSON → canvas rows. */
  | { type: 'SET_SECTIONS_DIRECT'; sections: CanvasSection[] }
  /** Remove a section by canonical key (content discarded — never merged). */
  | { type: 'REMOVE_SECTION'; sectionKey: string }
  /** Snapshot current sections as originalSections (before optimization). */
  | { type: 'SNAPSHOT_ORIGINAL_SECTIONS' }
  /** Insert or update a single section by title (case-insensitive match). */
  | { type: 'UPSERT_SECTION'; title: string; content: string }
  | { type: 'SET_ORIGINAL_RESUME'; data: ResumeData }
  | { type: 'SET_CANONICAL_RESUME'; data: CanonicalResume | null }
  | { type: 'SET_RAW_RESUME_TEXT'; text: string }
  | { type: 'SET_ANALYSIS'; analysis: AnalysisResult }
  | { type: 'SET_PENDING_SECTIONS'; sections: string[] }
  | { type: 'START_STREAMING'; section: string }
  | { type: 'APPEND_STREAM'; content: string }
  | { type: 'STOP_STREAMING' }
  | { type: 'SET_COVER_LETTER'; content: string }
  | { type: 'SET_APPLICATION_NOTE'; content: string }
  | { type: 'SELECT_JOBS'; jobs: JobContext[] }
  | { type: 'SET_ACTIVE_VERSION'; index: number }
  | { type: 'ADD_VERSION'; job: JobContext; sections: CanvasSection[] }
  | { type: 'SET_SAVING'; isSaving: boolean }
  | { type: 'SET_SAVED'; isSaved: boolean }
  | { type: 'SET_APPLIED'; isApplied: boolean }
  | { type: 'SET_COMMITTED_SECTIONS'; sections: CanvasSection[] }
  | { type: 'COMMIT_DRAFT' }
  | { type: 'DISCARD_DRAFT' }
  | { type: 'SET_DOCUMENT_LOCALE'; locale: DocumentLocale; source: DocumentLocaleSource }
  | { type: 'SET_ACTIVE_SECTION'; title: string }
  | { type: 'RESET_CANVAS' }
  | { type: 'BACK_TO_LIST' };

const initialState: CanvasState = {
  mode: 'LIST',
  tab: 'resume',
  templateId: 'ats-optimized',
  phase: 'idle',
  job: null,
  sections: null,
  originalSections: null,
  pendingSections: [],
  originalResumeData: null,
  canonicalResume: null,
  rawResumeText: '',
  analysis: null,
  originalMatchScore: null,
  streamingSection: null,
  streamingContent: '',
  coverLetter: null,
  applicationNote: null,
  selectedJobs: [],
  activeVersionIndex: 0,
  versions: [],
  isSaving: false,
  isSaved: false,
  isApplied: false,
  committedSections: null,
  isDraftDirty: false,
  documentLocale: 'en',
  documentLocaleSource: 'auto',
};

function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function markDraftIfChanged(
  state: CanvasState,
  nextSections: CanvasSection[],
): Pick<CanvasState, 'sections' | 'isDraftDirty'> {
  const committed = state.committedSections;
  const dirty =
    committed != null &&
    JSON.stringify(committed) !== JSON.stringify(nextSections);
  return { sections: nextSections, isDraftDirty: dirty || (committed == null && nextSections.length > 0) };
}

function canvasReducer(state: CanvasState, action: CanvasAction): CanvasState {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.mode };

    case 'SET_TAB':
      return { ...state, tab: action.tab };

    case 'SET_TEMPLATE':
      return { ...state, templateId: action.templateId };

    case 'SET_PHASE': {
      if (state.phase === action.phase) return state;
      const phaseUpdate: Partial<CanvasState> = { phase: action.phase };
      if (action.phase === 'optimization' || action.phase === 'consent') {
        phaseUpdate.canonicalResume = null;
      }
      return { ...state, ...phaseUpdate };
    }

    case 'SET_JOB': {
      const detected = detectLanguageFromJob(action.job);
      return {
        ...state,
        job: action.job,
        mode: 'CANVAS',
        phase: 'analysis',
        canonicalResume: null,
        isSaved: false,
        isApplied: false,
        documentLocale: detected,
        documentLocaleSource: 'auto',
      };
    }

    case 'SET_DOCUMENT_LOCALE': {
      const relabeled = state.sections
        ? relabelCanvasSections(state.sections, action.locale)
        : null;
      const relabeledOriginal = state.originalSections
        ? relabelCanvasSections(state.originalSections, action.locale)
        : null;
      const relabeledCommitted = state.committedSections
        ? relabelCanvasSections(state.committedSections, action.locale)
        : null;
      return {
        ...state,
        documentLocale: action.locale,
        documentLocaleSource: action.source,
        ...(relabeled ? { sections: relabeled } : {}),
        ...(relabeledOriginal ? { originalSections: relabeledOriginal } : {}),
        ...(relabeledCommitted ? { committedSections: relabeledCommitted } : {}),
      };
    }

    case 'SNAPSHOT_ORIGINAL_SECTIONS':
      return { ...state, originalSections: state.sections ? [...state.sections] : null };

    case 'SET_PENDING_SECTIONS':
      return { ...state, pendingSections: action.sections };

    case 'UPSERT_SECTION': {
      const existing = state.sections ?? [];
      const cleanContent = sanitizeSectionContentForCanvas(action.content, action.title);
      const idx = existing.findIndex(
        (s) => s.title.toLowerCase() === action.title.toLowerCase(),
      );
      let nextSections: CanvasSection[];
      if (idx >= 0) {
        const updated = [...existing];
        updated[idx] = { title: action.title, content: cleanContent };
        nextSections = dedupeCanvasSections(updated);
      } else {
        nextSections = dedupeCanvasSections([...existing, { title: action.title, content: cleanContent }]);
      }
      // Keep sections in canonical resume order (Personal → Summary → Experience → Education → Skills → ...)
      nextSections = sortSectionsByCanonicalOrder(nextSections);
      return { ...state, canonicalResume: null, ...markDraftIfChanged(state, nextSections) };
    }

    case 'SET_SECTIONS': {
      const nextSections = sortSectionsByCanonicalOrder(
        dedupeCanvasSections(
          cleanupMisplacedCanvasSections(
            action.sections.map((s) => ({
              title: s.title,
              content: sanitizeSectionContentForCanvas(s.content, s.title),
            })),
          ),
        ),
      );
      return { ...state, ...markDraftIfChanged(state, nextSections) };
    }

    case 'SET_SECTIONS_DIRECT': {
      if (state.phase === 'optimization' || state.phase === 'consent') {
        return state;
      }
      const nextSections = sortSectionsByCanonicalOrder(
        cleanupMisplacedCanvasSections(
          action.sections.map((s) => ({
            title: s.title,
            content: sanitizeSectionContentForCanvas(s.content, s.title),
          })),
        ),
      );
      const draft = markDraftIfChanged(state, nextSections);
      if (sameJson(state.sections, draft.sections) && state.isDraftDirty === draft.isDraftDirty) {
        return state;
      }
      return { ...state, ...draft };
    }

    case 'REMOVE_SECTION': {
      const existing = state.sections ?? [];
      const idx = findSectionIndexByKey(existing, action.sectionKey);
      if (idx < 0) return state;
      const removedTitle = existing[idx]!.title;
      const result = removeSectionByKey(existing, action.sectionKey);
      const stopStream =
        Boolean(state.streamingSection) &&
        state.streamingSection!.toLowerCase() === removedTitle.toLowerCase();
      return {
        ...state,
        sections: result.sections,
        ...markDraftIfChanged(state, result.sections),
        ...(stopStream ? { streamingSection: null, streamingContent: '' } : {}),
      };
    }

    case 'SET_ORIGINAL_RESUME':
      return { ...state, originalResumeData: action.data };

    case 'SET_CANONICAL_RESUME':
      if (sameJson(state.canonicalResume, action.data)) return state;
      return { ...state, canonicalResume: action.data };

    case 'SET_RAW_RESUME_TEXT':
      return { ...state, rawResumeText: action.text };

    case 'SET_ANALYSIS': {
      const nextOriginal = state.originalMatchScore ?? action.analysis.matchScore;
      if (
        sameJson(state.analysis, action.analysis) &&
        state.originalMatchScore === nextOriginal &&
        state.templateId === action.analysis.suggestedTemplate
      ) {
        return state;
      }
      return {
        ...state,
        analysis: action.analysis,
        // Lock the first-ever score as the "before" baseline. Subsequent
        // SET_ANALYSIS dispatches (post-optimization recompute) leave this alone.
        originalMatchScore: nextOriginal,
        templateId: action.analysis.suggestedTemplate,
      };
    }

    case 'START_STREAMING':
      return {
        ...state,
        streamingSection: action.section,
        streamingContent: '',
        pendingSections: state.pendingSections.filter(
          (s) => s.toLowerCase() !== action.section.toLowerCase()
        ),
      };

    case 'APPEND_STREAM':
      return {
        ...state,
        streamingContent: state.streamingContent + action.content,
      };

    case 'STOP_STREAMING':
      return {
        ...state,
        streamingSection: null,
        streamingContent: '',
      };

    case 'SET_COVER_LETTER':
      return { ...state, coverLetter: action.content };

    case 'SET_APPLICATION_NOTE':
      return { ...state, applicationNote: action.content };

    case 'SELECT_JOBS':
      return { ...state, selectedJobs: action.jobs };

    case 'SET_ACTIVE_VERSION':
      return { ...state, activeVersionIndex: action.index };

    case 'ADD_VERSION':
      return {
        ...state,
        versions: [...state.versions, { job: action.job, sections: action.sections }],
      };

    case 'SET_SAVING':
      return { ...state, isSaving: action.isSaving };

    case 'SET_SAVED':
      return { ...state, isSaved: action.isSaved, isSaving: false };

    case 'SET_APPLIED':
      return { ...state, isApplied: action.isApplied };

    case 'SET_COMMITTED_SECTIONS':
      return {
        ...state,
        committedSections: dedupeCanvasSections(
          action.sections.map((s) => ({
            title: s.title,
            content: sanitizeSectionContentForCanvas(s.content, s.title),
          })),
        ),
        isDraftDirty: false,
        isSaved: true,
      };

    case 'COMMIT_DRAFT':
      if (!state.sections?.length) return state;
      return {
        ...state,
        committedSections: [...state.sections],
        isDraftDirty: false,
        isSaved: true,
      };

    case 'DISCARD_DRAFT':
      if (!state.committedSections) return { ...state, isDraftDirty: false };
      return {
        ...state,
        sections: [...state.committedSections],
        isDraftDirty: false,
      };

    case 'SET_ACTIVE_SECTION':
      return { ...state, activeSectionTitle: action.title || undefined };

    case 'RESET_CANVAS':
      return { ...initialState };

    case 'BACK_TO_LIST':
      return {
        ...state,
        mode: 'LIST',
        phase: 'idle',
        job: null,
        streamingSection: null,
        streamingContent: '',
      };

    default:
      return state;
  }
}

// ── Context ───────────────────────────────────────────────────────────────────

export type CanvasContextValue = {
  state: CanvasState;
  dispatch: React.Dispatch<CanvasAction>;

  // Convenience actions
  setMode: (mode: PanelMode) => void;
  setTab: (tab: CanvasTab) => void;
  setTemplate: (templateId: string) => void;
  setPhase: (phase: AgentPhase) => void;
  setDocumentLocale: (locale: DocumentLocale, source?: DocumentLocaleSource) => void;
  /** Stores context without opening the canvas — call during analysis. */
  prepareOptimization: (job: JobContext, resumeData: ResumeData, rawText: string) => void;
  /** Opens the canvas + populates Personal Information — call on user confirmation. */
  startOptimization: (job: JobContext, resumeData: ResumeData, rawText: string) => void;
  backToList: () => void;

  // Streaming controls
  startStreaming: (section: string) => void;
  appendStream: (content: string) => void;
  stopStreaming: () => void;

  // Stream ref for external access
  streamRef: React.MutableRefObject<{
    append: (content: string) => void;
    stop: () => void;
  }>;
};

// Re-export so existing imports from CanvasProvider still work
export { CanvasContext };

// ── Provider ──────────────────────────────────────────────────────────────────

export function CanvasProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(canvasReducer, initialState);

  const streamRef = useRef({
    append: (_content: string) => {},
    stop: () => {},
  });

  const setMode = useCallback((mode: PanelMode) => {
    dispatch({ type: 'SET_MODE', mode });
  }, []);

  const setTab = useCallback((tab: CanvasTab) => {
    dispatch({ type: 'SET_TAB', tab });
  }, []);

  const setTemplate = useCallback((templateId: string) => {
    dispatch({ type: 'SET_TEMPLATE', templateId });
  }, []);

  const setPhase = useCallback((phase: AgentPhase) => {
    dispatch({ type: 'SET_PHASE', phase });
  }, []);

  const setDocumentLocale = useCallback((locale: DocumentLocale, source: DocumentLocaleSource = 'manual') => {
    dispatch({ type: 'SET_DOCUMENT_LOCALE', locale, source });
  }, []);

  /**
   * Prepare optimization context WITHOUT opening the canvas panel.
   * Call this during analysis so data is ready; the canvas opens only when the user confirms.
   */
  const prepareOptimization = useCallback((job: JobContext, resumeData: ResumeData, rawText: string) => {
    dispatch({ type: 'SET_ORIGINAL_RESUME', data: resumeData });
    dispatch({ type: 'SET_RAW_RESUME_TEXT', text: rawText });
    dispatch({ type: 'SET_JOB', job });
    dispatch({ type: 'SET_PHASE', phase: 'analysis' });
  }, []);

  /**
   * Open the canvas panel and populate the Personal Information section.
   * Call this when the user confirms they want to start writing.
   */
  const startOptimization = useCallback((job: JobContext, resumeData: ResumeData, rawText: string) => {
    // Switch right panel from job list to canvas view and always land on Resume tab
    dispatch({ type: 'SET_MODE', mode: 'CANVAS' });
    dispatch({ type: 'SET_TAB', tab: 'resume' });
    dispatch({ type: 'SET_ORIGINAL_RESUME', data: resumeData });
    dispatch({ type: 'SET_RAW_RESUME_TEXT', text: rawText });
    dispatch({ type: 'SET_JOB', job });
    dispatch({ type: 'SET_PHASE', phase: 'analysis' });

    // Build the initial "Personal Information" section — appears immediately while AI writes the rest
    const JUNK_RE = /^(not specified|not provided|n\/a|na|none|null|undefined|unknown|to be updated|tbd)$/i;
    const usable = (v: string | undefined | null): v is string => !!v && v.trim().length > 0 && !JUNK_RE.test(v.trim());
    const contactLines: string[] = [];
    if (usable(resumeData.name)) contactLines.push(resumeData.name);
    if (usable(resumeData.headline)) contactLines.push(resumeData.headline);
    if (usable(resumeData.contact?.email)) contactLines.push(resumeData.contact!.email!);
    if (usable(resumeData.contact?.phone)) contactLines.push(resumeData.contact!.phone!);
    if (usable(resumeData.contact?.location)) contactLines.push(resumeData.contact!.location!);
    if (usable(resumeData.contact?.linkedin)) contactLines.push(resumeData.contact!.linkedin!);
    if (usable(resumeData.contact?.github)) contactLines.push(resumeData.contact!.github!);
    if (usable(resumeData.contact?.portfolio)) contactLines.push(resumeData.contact!.portfolio!);

    const docLocale = detectLanguageFromJob(job);
    const initialSections: CanvasSection[] = contactLines.length > 0
      ? [{ title: sectionDisplayTitle('personal', docLocale), content: contactLines.join('\n') }]
      : [];

    dispatch({ type: 'SET_SECTIONS', sections: initialSections });
    window.dispatchEvent(new CustomEvent('ggj:canvas-focus', { detail: { tab: 'resume' } }));
  }, []);

  const backToList = useCallback(() => {
    dispatch({ type: 'BACK_TO_LIST' });
  }, []);

  const startStreaming = useCallback((section: string) => {
    const tab = section.trim().toLowerCase() === 'cover_letter'
      ? 'cover_letter' as const
      : section.trim().toLowerCase() === 'application_note'
        ? 'application_note' as const
        : 'resume' as const;
    dispatch({ type: 'SET_MODE', mode: 'CANVAS' });
    dispatch({ type: 'SET_TAB', tab });
    dispatch({ type: 'START_STREAMING', section });
    window.dispatchEvent(new CustomEvent('ggj:canvas-focus', { detail: { tab } }));
  }, []);

  const appendStream = useCallback((content: string) => {
    dispatch({ type: 'APPEND_STREAM', content });
  }, []);

  const stopStreaming = useCallback(() => {
    dispatch({ type: 'STOP_STREAMING' });
  }, []);

  // Update stream ref
  streamRef.current = {
    append: appendStream,
    stop: stopStreaming,
  };

  const value = useMemo(
    () => ({
      state,
      dispatch,
      setMode,
      setTab,
      setTemplate,
      setPhase,
      setDocumentLocale,
      prepareOptimization,
      startOptimization,
      backToList,
      startStreaming,
      appendStream,
      stopStreaming,
      streamRef,
    }),
    [state, setMode, setTab, setTemplate, setPhase, setDocumentLocale, prepareOptimization, startOptimization, backToList, startStreaming, appendStream, stopStreaming],
  );

  return <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>;
}

export default CanvasProvider;
